import { useEffect, useState } from 'react';
import { ROLES_PERSONAL, telefonoParaMostrar } from '@kodica/shared';

import { Iconos } from '../componentes/iconos';
import {
  Boton,
  Campo,
  Cargando,
  Celda,
  Chip,
  Encabezado,
  Entrada,
  ErrorCaja,
  Fila,
  Modal,
  Panel,
  Seleccion,
  Tabla,
  Vacio,
} from '../componentes/ui';
import { ErrorApi, pedir } from '../lib/api';
import { useRecurso } from '../lib/consultas';

/**
 * Quién trabaja en el condominio, y qué reja atiende cada guardia.
 *
 * No existía: dar de alta a un guardia exigía entrar a la base de datos. Y como
 * la asignación de su reja tampoco existía, el punto de acceso que veía se
 * elegía **en la propia caseta** y se guardaba en el navegador del aparato. Eso
 * no era una restricción —ese almacén lo edita cualquiera— sino una comodidad
 * que se le parecía.
 *
 * No están los residentes. Viven en el padrón, con su vivienda y su parentesco;
 * mezclarlos convertiría esta lista en un directorio de cientos de personas
 * donde hay que encontrar a tres guardias.
 */

interface Persona {
  id: string;
  nombre: string;
  apellidoPaterno: string | null;
  apellidoMaterno: string | null;
  email: string | null;
  telefono: string | null;
  rol: string;
  activo: boolean;
  ultimoAcceso: string | null;
  puntosAcceso: { id: string; nombre: string }[];
}

interface PuntoAcceso {
  id: string;
  nombre: string;
}

const ROL_TEXTO: Record<string, string> = {
  ADMIN_CONDOMINIO: 'Administración',
  JEFE_SEGURIDAD: 'Jefe de seguridad',
  GUARDIA: 'Guardia',
  SUPER_ADMIN: 'Kodica',
  RESIDENTE: 'Residente',
};

export function UsuariosPagina() {
  const { datos, cargando, error, recargar } = useRecurso<Persona[]>('/usuarios');
  const puntos = useRecurso<PuntoAcceso[]>('/topologia/puntos-acceso');
  const [editando, setEditando] = useState<Persona | null>(null);
  const [alta, setAlta] = useState(false);

  if (cargando) return <Cargando />;
  if (error) return <ErrorCaja>{error}</ErrorCaja>;

  const personas = datos ?? [];

  async function darDeBaja(p: Persona) {
    if (
      !window.confirm(
        `¿Dar de baja a ${p.nombre}? Deja de poder entrar, pero sigue en la bitácora de lo que autorizó.`,
      )
    )
      return;
    try {
      await pedir(`/usuarios/${p.id}`, { metodo: 'DELETE' });
      recargar();
    } catch (e) {
      window.alert(e instanceof ErrorApi ? e.message : 'No se pudo dar de baja');
    }
  }

  return (
    <>
      <Encabezado
        titulo="Personal"
        descripcion="Guardias, jefatura y administración. Los residentes van en el padrón."
      >
        <Boton variante="primario" icono={Iconos.agregar} onClick={() => setAlta(true)}>
          Agregar
        </Boton>
      </Encabezado>

      <Panel>
        {personas.length === 0 ? (
          <Vacio>Todavía no hay personal dado de alta.</Vacio>
        ) : (
          <Tabla cabeceras={['Nombre', 'Rol', 'Entra con', 'Rejas que atiende', '']}>
            {personas.map((p) => (
              <Fila key={p.id}>
                <Celda>
                  {[p.nombre, p.apellidoPaterno, p.apellidoMaterno].filter(Boolean).join(' ')}
                  {!p.activo && (
                    <>
                      {' '}
                      <Chip tono="neutro">baja</Chip>
                    </>
                  )}
                </Celda>
                <Celda>{ROL_TEXTO[p.rol] ?? p.rol}</Celda>
                {/* Con qué entra, porque no es lo mismo para todos: el guardia
                    teclea su teléfono en una tablet con guantes, y quien
                    administra usa su correo. */}
                <Celda>{p.telefono ? telefonoParaMostrar(p.telefono) : (p.email ?? '—')}</Celda>
                <Celda>{<Rejas p={p} />}</Celda>
                <Celda>
                  <div className="flex justify-end gap-1">
                    <Boton variante="texto" onClick={() => setEditando(p)}>
                      Editar
                    </Boton>
                    {p.activo && (
                      <Boton variante="texto" onClick={() => void darDeBaja(p)}>
                        Dar de baja
                      </Boton>
                    )}
                  </div>
                </Celda>
              </Fila>
            ))}
          </Tabla>
        )}
      </Panel>

      <ModalPersona
        abierto={alta || editando !== null}
        persona={editando}
        puntos={puntos.datos ?? []}
        alCerrar={() => {
          setAlta(false);
          setEditando(null);
        }}
        alGuardar={() => {
          setAlta(false);
          setEditando(null);
          recargar();
        }}
      />
    </>
  );
}

/**
 * Las rejas de un guardia, o por qué no tiene.
 *
 * **Un guardia sin asignar no ve ninguna visita**, y eso hay que decirlo aquí en
 * vez de dejar que se descubra a las tres de la mañana con una pantalla vacía y
 * una llamada de teléfono.
 */
function Rejas({ p }: { p: Persona }) {
  if (p.rol !== 'GUARDIA') {
    return <span className="text-on-surface-variant">Todo el condominio</span>;
  }
  if (p.puntosAcceso.length === 0) {
    return <Chip tono="malo">Sin asignar: no verá visitas</Chip>;
  }
  return <span>{p.puntosAcceso.map((r) => r.nombre).join(', ')}</span>;
}

function ModalPersona({
  abierto,
  persona,
  puntos,
  alCerrar,
  alGuardar,
}: {
  abierto: boolean;
  persona: Persona | null;
  puntos: PuntoAcceso[];
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const [campos, setCampos] = useState<Record<string, string>>({});
  const [rejas, setRejas] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const id = persona?.id;
  useEffect(() => {
    setCampos(
      persona
        ? {
            nombre: persona.nombre,
            apellidoPaterno: persona.apellidoPaterno ?? '',
            apellidoMaterno: persona.apellidoMaterno ?? '',
            email: persona.email ?? '',
            telefono: persona.telefono ?? '',
            rol: persona.rol,
          }
        : { rol: 'GUARDIA' },
    );
    setRejas(persona?.puntosAcceso.map((r) => r.id) ?? []);
    setError(null);
  }, [id, abierto, persona]);

  function set(k: string, v: string) {
    setCampos((c) => ({ ...c, [k]: v }));
  }

  const esGuardia = (campos['rol'] ?? 'GUARDIA') === 'GUARDIA';

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      const cuerpo: Record<string, unknown> = {
        nombre: campos['nombre'],
        apellidoPaterno: campos['apellidoPaterno'] || null,
        apellidoMaterno: campos['apellidoMaterno'] || null,
        email: campos['email'] || null,
        telefono: campos['telefono'] || null,
        rol: campos['rol'],
        puntosAcceso: esGuardia ? rejas : [],
      };
      // Al editar, la contraseña solo viaja si se escribió: mandarla vacía la
      // borraría, y mandarla siempre obligaría a retecleárla para cambiar un
      // apellido.
      if (campos['password']) cuerpo['password'] = campos['password'];

      await pedir(persona ? `/usuarios/${persona.id}` : '/usuarios', {
        metodo: persona ? 'PATCH' : 'POST',
        cuerpo,
      });
      alGuardar();
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      titulo={persona ? 'Editar persona' : 'Nueva persona'}
      abierto={abierto}
      alCerrar={alCerrar}
    >
      <form onSubmit={enviar} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <Campo etiqueta="Nombre">
            <Entrada
              required
              value={campos['nombre'] ?? ''}
              onChange={(e) => set('nombre', e.target.value)}
            />
          </Campo>
          <Campo etiqueta="Apellido paterno">
            <Entrada
              value={campos['apellidoPaterno'] ?? ''}
              onChange={(e) => set('apellidoPaterno', e.target.value)}
            />
          </Campo>
          <Campo etiqueta="Apellido materno">
            <Entrada
              value={campos['apellidoMaterno'] ?? ''}
              onChange={(e) => set('apellidoMaterno', e.target.value)}
            />
          </Campo>
        </div>

        <Campo etiqueta="Rol">
          <Seleccion
            value={campos['rol'] ?? 'GUARDIA'}
            onChange={(e) => set('rol', e.target.value)}
          >
            {ROLES_PERSONAL.map((r) => (
              <option key={r} value={r}>
                {ROL_TEXTO[r] ?? r}
              </option>
            ))}
          </Seleccion>
        </Campo>

        {/* Con qué entra. El guardia teclea su teléfono en una tablet con
            guantes a las tres de la mañana; un correo tiene arroba y se escribe
            mal. Por eso basta con uno de los dos, y el orden de los campos
            sigue al rol. */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo etiqueta="Teléfono" ayuda={esGuardia ? 'Con esto entra a la caseta' : 'Opcional'}>
            <Entrada
              type="tel"
              value={campos['telefono'] ?? ''}
              onChange={(e) => set('telefono', e.target.value)}
            />
          </Campo>
          <Campo etiqueta="Correo" ayuda={esGuardia ? 'Opcional' : 'Con esto entra al portal'}>
            <Entrada
              type="email"
              value={campos['email'] ?? ''}
              onChange={(e) => set('email', e.target.value)}
            />
          </Campo>
        </div>

        <Campo
          etiqueta="Contraseña"
          ayuda={
            persona
              ? 'Se deja en blanco para no cambiarla'
              : 'Al menos 8 caracteres. Se guarda cifrada y no se vuelve a mostrar.'
          }
        >
          <Entrada
            type="password"
            autoComplete="new-password"
            required={!persona}
            value={campos['password'] ?? ''}
            onChange={(e) => set('password', e.target.value)}
          />
        </Campo>

        {esGuardia && (
          <Campo
            etiqueta="Rejas que atiende"
            ayuda="Solo verá las visitas de estas rejas y solo podrá abrir sus plumas."
          >
            <div className="mt-1 space-y-1">
              {puntos.length === 0 && (
                <p className="text-body-md text-on-surface-variant">
                  No hay accesos dados de alta todavía.
                </p>
              )}
              {puntos.map((p) => (
                <label key={p.id} className="toque flex items-center gap-3">
                  <input
                    type="checkbox"
                    className="h-5 w-5"
                    checked={rejas.includes(p.id)}
                    onChange={(e) =>
                      setRejas((r) =>
                        e.target.checked ? [...r, p.id] : r.filter((x) => x !== p.id),
                      )
                    }
                  />
                  <span className="text-body-md text-on-surface">{p.nombre}</span>
                </label>
              ))}
              {rejas.length === 0 && puntos.length > 0 && (
                <p className="text-label-sm text-error">
                  Sin ninguna marcada no verá visitas ni podrá abrir nada.
                </p>
              )}
            </div>
          </Campo>
        )}

        {error && <ErrorCaja>{error}</ErrorCaja>}

        <div className="flex justify-end gap-2 pt-2">
          <Boton type="button" variante="texto" onClick={alCerrar}>
            Cancelar
          </Boton>
          <Boton type="submit" variante="primario" disabled={enviando}>
            {enviando ? 'Guardando…' : 'Guardar'}
          </Boton>
        </div>
      </form>
    </Modal>
  );
}
